import React, { useRef } from "react";
import PropTypes from "prop-types";

import MobileSubMenuItems from "./mobile-sub-menu-items";
import Button from "../custom-widgets/button";
import { Link } from "gatsby";
import Icon from "../custom-widgets/icon";
import MobileMenusSectionAboveMenus from "./mobile-menus-section-above-menus";

// URL Data
import { useLanguageContext } from "../../contexts/language-context";

// Helper Functions
import { getAlternateUrl } from "../../helpers/get-alternate-url";

/**
 * @typedef {Object} MenuData
 * @property {string} id - required id of the menu item link.
 * @property {string} title - required string title of the menu item link visible to the user.
 * @property {MenuData[]} menus - the same recursive object.
 * @property {string} externalLink - boolean value to use "anchor" if it is true.
 * @property {string} url - optional string link where the menu item should go in case we want to.
 * @property {string} landingTitle - this is the title shown at the top of the categories when the menu item have more links to show.
 * @property {string} showMenuItems - if this is true we will use the "menus" prop directly below this menu item and disable it.
 * @property {bool} show - boolean value to show or hide the menu item.
 */

/**
 * This renders the mobile menu that appears on the left and its menu items.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.path - required url path from the current page "/personal-banking/checking-account".
 * @param {Function} props.setShowMobileSearch - required function to show the search suggestions section within the mobile menu.
 * @param {MenuData[]} props.menuData - required menu data with the links or sub menus for the category in case it have some.
 * @returns {React.JSX.Element} Returns the JSX component to render
 */
const MobileMenuItems = ({ path = "", setShowMobileSearch = () => {}, menuData = [] }) => {
  const isSpanish = useLanguageContext();
  const mobileMenuItemsContainerRef = useRef(null);

  const altUrl = getAlternateUrl(path) || "/";

  const openAnAccountUrl = () => {
    // OLD CODE AS REFERENCE
    /*if (path === "/open-bank-account-online") {
      return "/open-an-account";
    } else if (path === "/business-banking/open-business-bank-account-online") {
      return "https://forms.fivision.com/wafdbank/oba/Default.aspx";
    } else if (path.indexOf("business-banking") > 0 || path.indexOf("commercial-banking") > 0) {
      return "/business-banking/open-business-bank-account-online";
    } else return "/open-bank-account-online";*/

    const businessCommercialBanking =
      path.indexOf("business-banking") > 0 || path.indexOf("commercial-banking") > 0 ? true : false;

    const OPEN_ACCOUNT_LINKS = {
      "/open-bank-account-online": "/open-an-account",
      "/business-banking/open-business-bank-account-online":
        "https://wafd.my.site.com/nPortal__SelfRegistration?core=es5"
    };

    const DEFAULT_URL = "/open-bank-account-online";

    const link = businessCommercialBanking
      ? "/business-banking/open-business-bank-account-online"
      : OPEN_ACCOUNT_LINKS[path] || DEFAULT_URL;
    //console.log("Returned link:", link);
    return link;
  };

  const oabUrl = openAnAccountUrl();
  const isExternalLink = oabUrl.startsWith("http") || oabUrl.startsWith("https");
  const openAccountBtn = {
    id: "mm-open-account-button",
    type: isExternalLink ? "anchor" : "link",
    text: "Open an Account",
    url: oabUrl,
    showIcon: false,
    class: "btn btn-primary d-block mb-3"
  };

  return (
    <div
      id="mobile-menu-items-main"
      className="mobile-menu-items-container"
      key="mobile-menu-items-main"
      ref={mobileMenuItemsContainerRef}
    >
      <MobileMenusSectionAboveMenus altUrl={altUrl} setShowMobileSearch={setShowMobileSearch} />

      {menuData.map((menuItem) => {
        return (
          <>
            {menuItem.show ? (
              <MobileSubMenuItems aboveMenuContainerRef={mobileMenuItemsContainerRef} path={path} menuData={menuItem} />
            ) : null}
          </>
        );
      })}
      <div className="p-3 bg-white">
        {isSpanish ? (
          <Link
            to="/es/abrir-cuenta-bancaria-en-linea"
            className="btn btn-primary d-block mb-3"
            id="mm-open-account-button"
          >
            Abrir una cuenta
          </Link>
        ) : (
          <>
            <Button {...openAccountBtn} />
            <div>
              <Icon lib="far" name="mobile" class="text-primary mr-2" />
              <Link to="/personal-banking/online-banking#mobile-app-section" id="mm-mobile-banking-app-link">
                Get the WaFd Mobile app
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileMenuItems;

MobileMenuItems.propTypes = {
  path: PropTypes.string.isRequired,
  setShowMobileSearch: PropTypes.func.isRequired,
  menuData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      menus: PropTypes.arrayOf(PropTypes.object), // the object uses the same shape of menuData array items
      externalLink: PropTypes.bool,
      url: PropTypes.string,
      landingTitle: PropTypes.string,
      showMenuItems: PropTypes.bool,
      show: PropTypes.bool
    })
  ).isRequired
};
